import type { ProposedEditData } from '@/components/layout/chat-pane.types'
import type { ModifiedFileItem } from '@/components/features/collapsible-modified-files-panel'
import { coalesceCompletedFiles } from '@/lib/chat-completed-files'
import { isPendingProposedEdit } from '@/lib/proposed-edit-utils'

export type ChatModifiedFileRow = ModifiedFileItem & {
  /** When set, row is a pending proposed_edit (not yet applied). */
  pendingEdit?: ProposedEditData
}

import type { CompletedFileRow } from '@/lib/chat-completed-files'

/** One pending row per document (later same-doc proposals replace earlier). */
function coalescePendingEditsByDocument(pendingEdits: ProposedEditData[]): ProposedEditData[] {
  const order: string[] = []
  const byDoc = new Map<string, ProposedEditData>()
  const anon: ProposedEditData[] = []
  for (const pe of pendingEdits.filter(isPendingProposedEdit)) {
    const id = pe.documentId ? String(pe.documentId) : ''
    if (!id) {
      anon.push(pe)
      continue
    }
    if (!byDoc.has(id)) order.push(id)
    const prev = byDoc.get(id)
    byDoc.set(id, {
      ...pe,
      original: prev?.original ?? pe.original,
    })
  }
  return [...order.map((id) => byDoc.get(id)!), ...anon]
}

export function buildChatModifiedFilesPanelRows(
  pendingEdits: ProposedEditData[],
  completedFiles: CompletedFileRow[],
): ChatModifiedFileRow[] {
  const fromEdits: ChatModifiedFileRow[] = coalescePendingEditsByDocument(pendingEdits).map((pe) => ({
    filePath: pe.filename || pe.displayPath || 'document.md',
    displayPath: pe.displayPath ?? null,
    url: pe.url ?? null,
    status: 'modified' as const,
    linesAdded: pe.additions ?? 0,
    linesRemoved: pe.deletions ?? 0,
    documentId: pe.documentId,
    pendingEdit: pe,
    pendingReview: true,
  }))

  const pendingIds = new Set(
    fromEdits.map((r) => r.documentId).filter((id): id is string => Boolean(id)),
  )
  const pendingPaths = new Set(fromEdits.map((r) => r.filePath))

  const fromCompleted: ChatModifiedFileRow[] = coalesceCompletedFiles(completedFiles)
    .filter((f) => {
      if (f.documentId && pendingIds.has(f.documentId)) return false
      if (pendingPaths.has(f.filePath)) return false
      return true
    })
    .map((f) => ({
      filePath: f.filePath,
      displayPath: f.displayPath,
      url: f.url,
      status: f.status ?? 'modified',
      linesAdded: f.linesAdded,
      linesRemoved: f.linesRemoved,
      documentId: f.documentId,
      pendingReview: false,
    }))

  return [...fromEdits, ...fromCompleted]
}
